import { useEffect, useRef, useState, type ReactNode } from "react";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import {
  NOME_DO_ANEXO,
  ROMANO,
  emPercentual,
  emReais,
  ordinalDaFaixa,
} from "../lib/simplesNacional";
import type { Cnae } from "../lib/radarSimples";

// As peças do Radar Simples. O que é regra mora nos módulos puros; aqui é só
// apresentação.
//
// ---------------------------------------------------------------------------
// A cor DENTRO do território navy
// ---------------------------------------------------------------------------
// A `Cabine` de `FerramentaUi` é `painel-navy`, escuro nos DOIS temas. Ali cor
// semântica é armadilha — `text-tinta` clareia no escuro e vira branco sobre
// branco, e `text-cinza` escurece no claro e some no navy. Sobre navy: cor de
// marca ou `navy-ink`/`navy-mute`.
//
// E texto pequeno alaranjado sobre fundo CLARO é `text-alerta-tinta`, nunca
// `text-laranja-texto`: medido, `#c76a00` dá 3,22 sobre `bg-tile` e 3,83 sobre a
// superfície branca — reprova como texto nos dois.

// ---------------------------------------------------------------------------
// Campo de dinheiro
// ---------------------------------------------------------------------------

/**
 * Campo de valor em reais.
 *
 * Guarda o TEXTO (que aparece) e o NÚMERO (que viaja) separados — a cicatriz do
 * campo de valor do IR Proposta, em que mandar `"R$ 1.234,56"` para uma coluna
 * `numeric` gravava NULO em silêncio, e o aluno via o campo vazio sem saber que
 * o cliente havia respondido.
 */
export function CampoMoeda({
  id,
  rotulo,
  valor,
  aoMudar,
  dica,
  placeholder = "R$ 0,00",
}: {
  id: string;
  rotulo: ReactNode;
  valor: number | null;
  aoMudar: (n: number | null) => void;
  dica?: ReactNode;
  placeholder?: string;
}) {
  const [texto, setTexto] = useState(() => moedaDeNumero(valor));

  // Quando o valor vem de fora (limpar, carregar do banco), o campo acompanha.
  // Os dois lugares que leem a fonte existem de propósito: sem o efeito, "Limpar"
  // não limpa; sem o estado inicial, o campo pisca vazio antes do primeiro render.
  useEffect(() => {
    setTexto((atual) => {
      const doValor = moedaDeNumero(valor);
      // Só sobrescreve quando o número mudou de verdade — senão digitar
      // "1.234,5" viraria "R$ 1.234,50" no meio da digitação.
      return mascararMoeda(atual).numero === valor ? atual : doValor;
    });
  }, [valor]);

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-tinta">
        {rotulo}
      </label>
      <input
        id={id}
        inputMode="numeric"
        placeholder={placeholder}
        value={texto}
        onChange={(e) => {
          const m = mascararMoeda(e.target.value);
          setTexto(m.texto);
          aoMudar(m.numero);
        }}
        className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta tabular-nums outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
      />
      {dica && <p className="text-xs text-cinza">{dica}</p>}
    </div>
  );
}

/** Campo de percentual — aceita vírgula, guarda número. */
export function CampoPercentual({
  id,
  rotulo,
  valor,
  aoMudar,
  dica,
}: {
  id: string;
  rotulo: ReactNode;
  valor: number;
  aoMudar: (n: number) => void;
  dica?: ReactNode;
}) {
  const [texto, setTexto] = useState(() => String(valor).replace(".", ","));
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-tinta">
        {rotulo}
      </label>
      <div className="relative">
        <input
          id={id}
          inputMode="decimal"
          value={texto}
          onChange={(e) => {
            // Só dígito e uma vírgula. Aceitar ponto deixaria "1.234" ambíguo,
            // que é a mesma armadilha da máscara de moeda solta.
            const limpo = e.target.value.replace(/[^\d,]/g, "").replace(/,(?=.*,)/g, "");
            setTexto(limpo);
            const n = Number(limpo.replace(",", "."));
            aoMudar(Number.isFinite(n) ? n : 0);
          }}
          className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 pr-8 text-tinta tabular-nums outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-cinza">
          %
        </span>
      </div>
      {dica && <p className="text-xs text-cinza">{dica}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Números e resultados
// ---------------------------------------------------------------------------

/** Um número grande com rótulo — o "herói" de um resultado. */
export function NumeroHeroi({
  rotulo,
  valor,
  apoio,
  tom = "neutro",
}: {
  rotulo: string;
  valor: string;
  apoio?: ReactNode;
  tom?: "neutro" | "sucesso" | "alerta";
}) {
  const cor =
    tom === "sucesso" ? "text-aviso-tinta" : tom === "alerta" ? "text-alerta-tinta" : "text-titulo";
  return (
    <div className="rounded-cf border border-borda bg-superficie p-4">
      <p className="text-xs uppercase tracking-[0.12em] text-cinza">{rotulo}</p>
      <p className={`mt-1 text-3xl font-bold tabular-nums ${cor}`}>{valor}</p>
      {apoio && <div className="mt-1 text-sm text-cinza">{apoio}</div>}
    </div>
  );
}

/** Uma linha de detalhamento: rótulo à esquerda, valor à direita. */
export function LinhaDeValor({
  rotulo,
  valor,
  nota,
  apagada,
  forte,
}: {
  rotulo: ReactNode;
  valor: string;
  nota?: ReactNode;
  apagada?: boolean;
  forte?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-1.5 ${
        forte ? "border-t border-borda pt-3 font-semibold text-tinta" : ""
      }`}
    >
      <span className={`text-sm ${apagada ? "text-cinza" : "text-tinta"}`}>
        {rotulo}
        {nota && (
          // A nota fica NA LINHA e é texto, não opacidade: desabilitado
          // permanente pede cor própria, e transparência comunica espera.
          <span className="ml-2 rounded-full border border-borda px-2 py-0.5 text-[11px] text-cinza">
            {nota}
          </span>
        )}
      </span>
      <span className={`shrink-0 text-sm tabular-nums ${apagada ? "text-cinza" : "text-tinta"}`}>
        {valor}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CNAE
// ---------------------------------------------------------------------------

const SELO: Record<string, { texto: string; classe: string }> = {
  Sim: { texto: "Permitido", classe: "bg-aviso-fundo text-aviso-tinta border-aviso-borda" },
  Não: { texto: "Vedado", classe: "bg-erro-fundo text-erro-tinta border-erro-tinta/30" },
  Revisar: { texto: "Revisar", classe: "bg-alerta-fundo text-alerta-tinta border-alerta-tinta/40" },
};

export function SeloDePermissao({ valor }: { valor: string }) {
  const s = SELO[valor] ?? SELO.Revisar;
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.classe}`}>
      {s.texto}
    </span>
  );
}

export function ChipDeAnexo({ anexo }: { anexo: number | null }) {
  if (anexo === null) return null;
  return (
    <span className="rounded-full border border-acento/30 bg-acento/12 px-2 py-0.5 text-[11px] font-medium text-acento">
      Anexo {ROMANO[anexo] ?? anexo}
    </span>
  );
}

/**
 * A LINHA de um CNAE na busca.
 *
 * ---------------------------------------------------------------------------
 * POR QUE LINHA E NÃO CARTÃO
 * ---------------------------------------------------------------------------
 * Nasceu cartão: código, chips, descrição, classificação, ressalva e um botão
 * "Usar nesta análise", ~170px cada. Medido em 05/09 com `capturas.mjs`, a
 * busca vazia (que mostra 50 de 1.333) saía com **8.521px** — nove telas e
 * meia —, com "Atividade rural, florestal, pesca ou aquicultura" repetida 50
 * vezes e "Usar nesta análise" outras 50.
 *
 * É a cicatriz do IR Pro, que foi de 8.249px para 1.120px com o MESMO conteúdo:
 * **lista densa de texto encontra melhor que grade de cartões**, e o uso real
 * aqui é procurar uma atividade pelo nome. Em linha são ~44px, e a mesma busca
 * cabe em pouco mais de duas telas.
 *
 * O que saiu da linha, e por quê:
 *
 *  · o chip **`tipo`** — ele é DERIVADO do anexo (Comércio↔I, Indústria↔II,
 *    Serviço↔III/IV/V, medido no banco em 05/09), então mostrá-lo ao lado do
 *    chip "Anexo I" é dizer a mesma coisa duas vezes. E ele está ERRADO em 122
 *    linhas: toda atividade rural cai no Anexo I e sai rotulada "Comércio" —
 *    é o que faz "Cultivo de arroz" aparecer como comércio na tela.
 *  · o selo **"Permitido"** quando é permitido: são 1.237 de 1.333. Selo que
 *    aparece em 93% das linhas não distingue nada; o que informa é a ausência
 *    dele. Vedado e "a revisar" continuam à vista.
 *  · a **classificação** e o texto da **ressalva**, que vão para a ficha. A
 *    ressalva deixa um ponto de aviso na linha, para não sumir em silêncio —
 *    são 26 em 1.333, e é justamente por serem raras que importam.
 */
export function LinhaDeCnae({
  cnae,
  escolhido,
  aoAlternar,
  aoAbrir,
}: {
  cnae: Cnae;
  escolhido?: boolean;
  aoAlternar?: () => void;
  aoAbrir?: () => void;
}) {
  return (
    <div
      data-codigo={cnae.codigo}
      className={`flex items-center gap-3 rounded-cf border px-3 py-2 transition ${
        escolhido ? "border-acento bg-acento/8" : "border-transparent hover:bg-tile"
      }`}
    >
      <button
        type="button"
        onClick={aoAbrir}
        className="flex min-w-0 flex-1 items-baseline gap-3 text-left"
        aria-label={`Ver a ficha de ${cnae.codigo} — ${cnae.descricao}`}
      >
        <span className="shrink-0 font-mono text-xs font-semibold text-titulo">{cnae.codigo}</span>
        {/* `truncate` mais `title`: o nome inteiro fica disponível sem que a
            linha cresça. Descrição de CNAE chega a 90 caracteres. */}
        <span className="min-w-0 flex-1 truncate text-sm text-tinta" title={cnae.descricao}>
          {cnae.descricao}
        </span>
      </button>

      <div className="flex shrink-0 items-center gap-1.5">
        {cnae.ressalva && (
          <span
            aria-label="Tem ressalva — veja a ficha"
            title={cnae.ressalva}
            className="text-xs text-alerta-tinta"
          >
            ▲
          </span>
        )}
        {cnae.exige_fator_r && (
          <span className="hidden rounded-full border border-alerta-tinta/40 bg-alerta-fundo px-2 py-0.5 text-[11px] font-medium text-alerta-tinta sm:inline">
            Fator R
          </span>
        )}
        {cnae.permitido_simples !== "Sim" && <SeloDePermissao valor={cnae.permitido_simples} />}
        <ChipDeAnexo anexo={cnae.anexo_principal} />
      </div>

      {aoAlternar && (
        <button
          type="button"
          onClick={aoAlternar}
          data-escolher={cnae.codigo}
          aria-pressed={Boolean(escolhido)}
          title={escolhido ? "Remover da análise" : "Usar nesta análise"}
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition ${
            escolhido
              ? "border border-acento bg-acento text-acento-tinta"
              : "border border-borda text-cinza hover:border-acento/50 hover:text-link"
          }`}
        >
          {/* O rótulo curto na linha, o longo no `title` e no leitor de tela: em
              50 linhas, "Usar nesta análise" repetido é a maior fonte de ruído
              da tela — foi o segundo texto mais repetido da medição. */}
          <span aria-hidden>{escolhido ? "✓ Escolhido" : "+ Usar"}</span>
          <span className="sr-only">
            {escolhido ? `Remover ${cnae.codigo} da análise` : `Usar ${cnae.codigo} nesta análise`}
          </span>
        </button>
      )}
    </div>
  );
}
// ---------------------------------------------------------------------------
// Tabelas
// ---------------------------------------------------------------------------

/**
 * Tabela que rola dentro do próprio container.
 *
 * O corpo da página nunca rola na horizontal — é regra da casa, e uma tabela de
 * 9 colunas em 360px de largura é exatamente o que a quebraria.
 */
export function TabelaRolante({ children, rotulo }: { children: ReactNode; rotulo: string }) {
  return (
    <div className="overflow-x-auto rounded-cf border border-borda">
      <table className="w-full min-w-[36rem] border-collapse text-sm" aria-label={rotulo}>
        {children}
      </table>
    </div>
  );
}

export function Cabecalho({ colunas }: { colunas: ReactNode[] }) {
  return (
    <thead>
      <tr className="border-b border-borda bg-chip text-left">
        {colunas.map((c, i) => (
          <th
            key={i}
            scope="col"
            className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-cinza ${
              i === 0 ? "" : "text-right"
            }`}
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

// ---------------------------------------------------------------------------
// Avisos
// ---------------------------------------------------------------------------

/**
 * Um aviso de atenção.
 *
 * A família é `alerta` (laranja), NUNCA `aviso` — `--cf-aviso-*` nesta casa é
 * VERDE, a cor de confirmação, e um alerta verde diz o contrário do que o texto
 * diz. Foi a cicatriz do "Este processo não foi lido por inteiro" do Leitor.
 *
 * E a família `alerta` tem só `-fundo` e `-tinta`. A borda sai de
 * `border-alerta-tinta/40`, com opacidade — pedir uma variante `borda` a ela
 * produziria classe fantasma, que nem o `tsc` nem a suíte veem, e que só
 * `tests/tokens.mjs` pega. (Ele pegou esta linha quando o exemplo estava escrito
 * por extenso aqui: a varredura não distingue comentário de JSX, e o certo é o
 * comentário não conter a forma proibida.)
 */
export function Alerta({ children, titulo }: { children: ReactNode; titulo?: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-3 py-2.5 text-sm text-alerta-tinta">
      <span aria-hidden="true" className="mt-0.5">
        ▲
      </span>
      <div>
        {titulo && <strong className="block">{titulo}</strong>}
        {children}
      </div>
    </div>
  );
}

/** Uma confirmação — aqui sim a família verde. */
export function Confirmacao({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-cf border border-aviso-borda bg-aviso-fundo px-3 py-2.5 text-sm text-aviso-tinta">
      <span aria-hidden="true" className="mt-0.5">
        ✓
      </span>
      <div>{children}</div>
    </div>
  );
}

/** Uma nota de rodapé com a base legal. */
export function BaseLegal({ children }: { children: ReactNode }) {
  return <p className="text-xs text-cinza">📖 {children}</p>;
}

// ---------------------------------------------------------------------------
// Botões
// ---------------------------------------------------------------------------

export function BotaoPrimario({
  children,
  aoClicar,
  desligado,
  tipo = "button",
  ...resto
}: {
  children: ReactNode;
  aoClicar?: () => void;
  desligado?: boolean;
  tipo?: "button" | "submit";
} & Record<string, unknown>) {
  return (
    <button
      type={tipo}
      onClick={aoClicar}
      disabled={desligado}
      // `pill-desligada` para o desabilitado PERMANENTE: medido, texto azul
      // sobre laranja com `opacity-50` dá 1,57 : 1 sobre navy e 2,73 sobre
      // branco — reprova nos dois. Transparência comunica espera, não ausência
      // de permissão.
      className={desligado ? "pill-desligada" : "pill-cta"}
      {...resto}
    >
      {children}
    </button>
  );
}

export function BotaoSecundario({
  children,
  aoClicar,
  ...resto
}: { children: ReactNode; aoClicar?: () => void } & Record<string, unknown>) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      className="rounded-full border border-borda bg-superficie px-4 py-2 text-sm font-medium text-tinta transition hover:border-acento/40 hover:text-link"
      {...resto}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// A faixa de resultado por anexo
// ---------------------------------------------------------------------------

export function ResumoDoAnexo({ anexo }: { anexo: number }) {
  return (
    <p className="text-sm font-semibold text-tinta">{NOME_DO_ANEXO[anexo] ?? `Anexo ${anexo}`}</p>
  );
}

export { emReais, emPercentual, ordinalDaFaixa };

/**
 * Copia um texto e diz que copiou.
 *
 * O gesto central de um link que vai para o WhatsApp do cliente. O aviso some
 * sozinho — e o `useRef` do timer existe para o componente desmontado não
 * chamar `setEstado`.
 */
export function BotaoCopiar({ texto, rotulo = "Copiar link" }: { texto: string; rotulo?: string }) {
  const [copiou, setCopiou] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  return (
    <button
      type="button"
      data-copiar
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(texto);
          setCopiou(true);
          if (timer.current) window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopiou(false), 2500);
        } catch {
          // Sem permissão de área de transferência (contexto inseguro, ou o
          // usuário negou). O link continua visível na tela para copiar à mão —
          // por isso não há mensagem de erro: não há nada a fazer com ela.
        }
      }}
      className="rounded-full border border-borda bg-superficie px-3 py-1.5 text-xs font-medium text-tinta transition hover:border-acento/40 hover:text-link"
    >
      {copiou ? "Copiado" : rotulo}
    </button>
  );
}
